// src/components/Home.js
import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import FAQBanner from '../../components/Banner/FAQBanner';
import LeftSidebar from '../../components/Common/LeftSidebar';
import {FAQ_ITEMS, PATH} from "../../constants";
import {FAQ_INFO_DATA} from "../../data/faqInfo";
import {FAQ_ACCOUNT_MANAGEMENT} from "../../data/faqAccountManagement";
import {FAQ_TOPUP} from "../../data/faqTopUp";
import {FAQ_TRANSFER} from "../../data/faqTransfer";
import {FAQ_INCIDENT} from "../../data/faqIncident";
import {FAQ_SAFETY_SECURE} from "../../data/faqSafetySecure";
import './FAQ.css';

const FAQ = () => {
	const location = useLocation();
	const questionRefs = useRef({});
	const isUserInteraction = useRef(false); // Flag to track user interactions
	
	// Determine initial category based on URL
	const getInitialCategory = () => {
		const path = location.pathname;
		if (path === PATH.FAQ_INFO) return 'FAQ_INFO';
		if (path === PATH.FAQ_ACCOUNT) return 'FAQ_ACCOUNT_MANAGEMENT';
		if (path === PATH.FAQ_TOP_UP) return 'FAQ_TOP_UP';
		if (path === PATH.FAQ_TRANSFER) return 'FAQ_TRANSFER';
		if (path === PATH.FAQ_INCIDENT) return 'FAQ_INCIDENT';
		if (path === PATH.FAQ_SAFETY_SECURE) return 'FAQ_SAFETY_SECURE';
		return 'FAQ_INFO'; // Default
	};
	
	const [selectedCategory, setSelectedCategory] = useState(getInitialCategory());
	const [expandedQuestions, setExpandedQuestions] = useState('cau-1');
	const [showMobileMenu, setShowMobileMenu] = useState(false);
	
	// FAQ data structure
	const faqData = {
		FAQ_INFO: FAQ_INFO_DATA,
		FAQ_ACCOUNT_MANAGEMENT: FAQ_ACCOUNT_MANAGEMENT,
		FAQ_TOP_UP: FAQ_TOPUP,
		FAQ_TRANSFER: FAQ_TRANSFER,
		FAQ_INCIDENT: FAQ_INCIDENT,
		FAQ_SAFETY_SECURE: FAQ_SAFETY_SECURE
		// Add other categories here as needed
	};
	
	// Scroll to element with smooth animation
	const scrollToElement = (elementId) => {
		const element = questionRefs.current[elementId];
		if (element) {
			// Add a small delay to ensure the content is rendered
			setTimeout(() => {
				const headerHeight = 80; // Approximate header height in pixels
				const elementPosition = element.offsetTop - headerHeight - 20; // 20px extra padding
				
				window.scrollTo({
					top: elementPosition,
					behavior: 'smooth'
				});
			}, 100);
		} else {
			// If element is not found, retry after a longer delay
			// This can happen when navigating from other pages
			setTimeout(() => {
				const retryElement = questionRefs.current[elementId];
				if (retryElement) {
					const headerHeight = 80;
					const elementPosition = retryElement.offsetTop - headerHeight - 20;
					
					window.scrollTo({
						top: elementPosition,
						behavior: 'smooth'
					});
				}
			}, 1000); // Increased delay for better reliability
		}
	};
	
	// URL routing and hash navigation
	useEffect(() => {
		const handleHashChange = () => {
			const hash = window.location.hash.replace('#', '');
			if (hash) {
				// Check if it's a direct question link
				if (hash.startsWith('cau-')) {
					setExpandedQuestions(hash);
					// Determine the correct category based on current URL path
					const currentPath = window.location.pathname;
					let correctCategory = 'FAQ_INFO'; // Default
					
					if (currentPath === PATH.FAQ_INFO) {
						correctCategory = 'FAQ_INFO';
					} else if (currentPath === PATH.FAQ_ACCOUNT) {
						correctCategory = 'FAQ_ACCOUNT_MANAGEMENT';
					} else if (currentPath === PATH.FAQ_TOP_UP) {
						correctCategory = 'FAQ_TOP_UP';
					} else if (currentPath === PATH.FAQ_TRANSFER) {
						correctCategory = 'FAQ_TRANSFER';
					} else if (currentPath === PATH.FAQ_INCIDENT) {
						correctCategory = 'FAQ_INCIDENT';
					} else if (currentPath === PATH.FAQ_SAFETY_SECURE) {
						correctCategory = 'FAQ_SAFETY_SECURE';
					}
					
					// Only update category if it's different from current
					if (selectedCategory !== correctCategory) {
						setSelectedCategory(correctCategory);
					}
					
					// Only scroll if this is NOT a user interaction (direct URL access)
					if (!isUserInteraction.current) {
						scrollToElement(hash);
					}
					
					// Reset the flag
					isUserInteraction.current = false;
				}
			}
		};
		
		// Handle initial load
		handleHashChange();
		
		// Listen for hash changes
		window.addEventListener('hashchange', handleHashChange);
		return () => window.removeEventListener('hashchange', handleHashChange);
	}, [selectedCategory]);
	
	// Handle location changes (navigation from other pages)
	useEffect(() => {
		const hash = location.hash.replace('#', '');
		if (hash && hash.startsWith('cau-')) {
			// Set the expanded question
			setExpandedQuestions(hash);
			
			// Scroll to element when navigating from other pages (direct URL access)
			const scrollTimer = setTimeout(() => {
				scrollToElement(hash);
			}, 800); // Longer delay to ensure content is fully rendered
			
			// Cleanup timer if component unmounts
			return () => clearTimeout(scrollTimer);
		}
	}, [location.pathname, location.hash]);
	
	// Additional effect to handle scrolling when selectedCategory changes due to navigation
	useEffect(() => {
		const hash = location.hash.replace('#', '');
		if (hash && hash.startsWith('cau-') && expandedQuestions === hash) {
			// Don't auto-scroll when selecting subItems - only scroll on direct URL access
		}
	}, [selectedCategory, expandedQuestions, location.hash]);
	
	// Update URL when expanding questions
	const expandQuestion = (questionId, updateUrl = true) => {
		
		console.log('expandQuestion', questionId);
		
		
		isUserInteraction.current = true; // Mark as user interaction
		
		// Check if questionId is a subItem (not a contentItem)
		const subItem = faqData[selectedCategory]?.subItems.find(subItem => subItem.id === questionId);
		if (subItem?.contentItems && subItem.contentItems.length > 0) {
			// If it's a subItem, auto-expand the first contentItem
			const firstContentItem = subItem.contentItems[0];
			setExpandedQuestions(firstContentItem.id);
			
			// Update URL with hash
			if (updateUrl) {
				const categoryPath = getCategoryPath(selectedCategory);
				const newUrl = `${categoryPath}#${firstContentItem.id}`;
				window.history.pushState(null, '', newUrl);
			}
			return;
		}
		
		// If it's a contentItem, expand it directly
		setExpandedQuestions(questionId);
		
		// Only update URL if requested (prevents auto-scroll from left sidebar)
		if (updateUrl) {
			// Update URL with hash
			const categoryPath = getCategoryPath(selectedCategory);
			const newUrl = `${categoryPath}#${questionId}`;
			
			// Update URL without page reload
			window.history.pushState(null, '', newUrl);
		}
		
		// Don't auto-scroll when clicking - only scroll on initial page load with hash
	};
	
	// Get category path for URL updates
	const getCategoryPath = (categoryId) => {
		switch (categoryId) {
			case 'FAQ_INFO':
				return PATH.FAQ_INFO;
			case 'FAQ_ACCOUNT_MANAGEMENT':
				return PATH.FAQ_ACCOUNT;
			case 'FAQ_TOP_UP':
				return PATH.FAQ_TOP_UP;
			case 'FAQ_TRANSFER':
				return PATH.FAQ_TRANSFER;
			case 'FAQ_INCIDENT':
				return PATH.FAQ_INCIDENT;
			case 'FAQ_SAFETY_SECURE':
				return PATH.FAQ_SAFETY_SECURE;
			default:
				return PATH.FAQ;
		}
	};
	
	// Handle category selection with URL update
	const selectCategory = (categoryId) => {
		isUserInteraction.current = true; // Mark as user interaction
		setSelectedCategory(categoryId);
		
		// Update URL without navigation to prevent scroll to top
		const categoryPath = getCategoryPath(categoryId);
		window.history.pushState(null, '', categoryPath);
		
		// Auto-expand first sub-item when selecting a category
		if (faqData[categoryId]?.subItems && faqData[categoryId].subItems.length > 0) {
			const firstSubItem = faqData[categoryId].subItems[0];
			if (firstSubItem.contentItems && firstSubItem.contentItems.length > 0) {
				const firstContentItem = firstSubItem.contentItems[0];
				setExpandedQuestions(firstContentItem.id);
				
				// Update URL with hash
				const newUrl = `${categoryPath}#${firstContentItem.id}`;
				window.history.pushState(null, '', newUrl);
				
				// Don't auto-scroll when selecting category - only scroll on initial page load with hash
			} else {
				setExpandedQuestions(firstSubItem.id);
			}
		} else {
			setExpandedQuestions('');
		}
	};
	
	const currentCategory = faqData[selectedCategory];
	return (
		<div className="mx-auto w-full animate-fadeIn text-sm">
			<FAQBanner/>
			
			{/* Main Content */}
			<div className="faq-container px-4 py-8">
				<div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
					<LeftSidebar
						title="Câu hỏi theo chủ đề"
						items={FAQ_ITEMS}
						selectedItem={selectedCategory}
						onItemSelect={selectCategory}
						showMobileMenu={showMobileMenu}
						setShowMobileMenu={setShowMobileMenu}
						faqData={faqData}
						expandedQuestions={expandedQuestions}
						onSubItemSelect={expandQuestion}
						showSubItems={true}
					/>
					
					{/* Main Content - Questions & Answers */}
					<div className="w-full lg:w-2/3">
						<div className="faq-content p-4">
							<h2 className="font-bold text-xl text-gray-800 mb-6">
								{currentCategory?.title || 'Thông tin cơ bản'}
							</h2>
							
							{/* Show selected sub-item title and its children */}
							{faqData[selectedCategory]?.subItems && (
								<>
									{faqData[selectedCategory].subItems.map((subItem) => {
										const hasExpandedContent = expandedQuestions && subItem.contentItems.some(contentItem => contentItem.id === expandedQuestions);
										if (subItem.id === expandedQuestions || hasExpandedContent) {
											return (
												<div key={subItem.id} className="mb-6">
													<h3 className="font-bold text-lg text-gray-800 mb-4">
														{subItem.title}
													</h3>
													<div className="space-y-4">
														{subItem.contentItems.map((contentItem) => (
															<div
																key={contentItem.id}
																className="bg-[#FAFAFC]"
																ref={(el) => {
																	questionRefs.current[contentItem.id] = el;
																}}
															>
																<button
																	onClick={() => {
																		expandQuestion(expandedQuestions === contentItem.id ? subItem.id : contentItem.id);
																	}}
																	className="faq-question w-full text-left flex items-center p-3 rounded-lg"
																>
																	<img
																		className={`faq-chevron w-5 h-5 mr-2 ${expandedQuestions === contentItem.id ? 'rotate-90' : ''}`}
																		src="/img/icon/arrow.png"
																		alt="expand"
																	/>
																	<span className="font-bold text-gray-800 text-sm">
																		{contentItem.title}
																	</span>
																</button>
																{expandedQuestions === contentItem.id && (
																	<div className="faq-answer pl-5 py-4">
																		<div className="text-gray-700 leading-relaxed text-sm">
																			{contentItem.content}
																		</div>
																	</div>
																)}
															</div>
														))}
													</div>
												</div>
											);
										}
										return null;
									})}
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FAQ;
